import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { FaCheckCircle, FaChartBar, FaFilePdf } from "react-icons/fa";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const EXPERIENCE_LABELS = {
  1: "Teaching Background",
  2: "Lesson Planning",
  3: "Teaching Methods Used",
  4: "Use of Technology in Teaching",
  5: "Classroom Management",
  6: "Assessment & Feedback",
  7: "Reflection & Professional Development",
};

const SCALE_LABELS = {
  1: "Strongly Disagree",
  2: "Disagree",
  3: "Neutral",
  4: "Agree",
  5: "Strongly Agree",
};

const getScoreLabel = (score) => {
  if (score <= 1.5) return "Very Low";
  if (score <= 2.5) return "Low";
  if (score <= 3.5) return "Moderate";
  if (score <= 4.5) return "High";
  return "Very High";
};

const getScoreColor = (score) => {
  if (score <= 1.5) return "bg-red-500";
  if (score <= 2.5) return "bg-orange-400";
  if (score <= 3.5) return "bg-yellow-300";
  if (score <= 4.5) return "bg-emerald-400";
  return "bg-green-500";
};

// Optional: simple recommendation templates per category
const RECOMMENDATION_TEXT = {
  1: "Consider strengthening your basic teaching background by reviewing core teaching principles and seeking mentoring from experienced teachers.",
  2: "Try to plan lessons more systematically, with clear objectives, prepared materials, and adjustments based on students' needs.",
  3: "Explore more varied teaching methods such as group work, projects, and inquiry-based learning to increase student engagement.",
  4: "Increase your use of digital tools, multimedia, and online platforms to enrich your teaching and assessment.",
  5: "Focus on building clear rules, positive communication, and proactive strategies to manage classroom behavior.",
  6: "Use varied assessments and provide regular, constructive feedback to help students understand their progress.",
  7: "Allocate time for reflection and join professional development activities to continuously improve your teaching.",
};

const TeachingExperienceSummary = () => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");
  const [studentId, setStudentId] = useState(null);
  const [studentInfo, setStudentInfo] = useState({
    name: "",
    nim: "",
  });

  const summaryRef = useRef(null);

  // Load student session (ID, Name, NIM) from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem("student");
      if (!stored) {
        setError("Session error: student data not found.");
        return;
      }

      const student = JSON.parse(stored);
      if (!student?.student_id || Number(student.student_id) <= 0) {
        setError("Session error: invalid student ID.");
        return;
      }

      setStudentId(student.student_id);

      // Try to get name and NIM from stored student object
      setStudentInfo({
        name:
          student.name ||
          student.full_name ||
          student.student_name ||
          "",
        nim:
          student.nim ||
          student.nim_ut ||
          student.student_number ||
          "",
      });
    } catch (err) {
      console.error(err);
      setError("Session error. Please log in again.");
    }
  }, []);

  // Fetch summary from backend
  useEffect(() => {
    if (!studentId) return;

    const fetchSummary = async () => {
      try {
        setLoading(true);
        setError("");

        const res = await fetch(
          `http://localhost:3000/api/teaching-experience/summary?student_id=${studentId}`
        );
        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.message || "Failed to fetch summary.");
        }

        setSummary(data);

        // Mark as done in localStorage
        const currentStudent = JSON.parse(localStorage.getItem("student"));
        if (currentStudent) {
          localStorage.setItem(
            "student",
            JSON.stringify({
              ...currentStudent,
              te_current_step: "done",
            })
          );
        }
      } catch (err) {
        console.error(err);
        setError(err.message || "Failed to load summary.");
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, [studentId]);

  const handleGoDashboard = () => {
    window.location.href = "/dashboard";
  };

  // Export current summary card as PDF
  const handleExportPDF = async () => {
    if (!summaryRef.current) return;

    const element = summaryRef.current;
    const canvas = await html2canvas(element, { scale: 2 });
    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

    pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
    pdf.save("teaching-experience-summary.pdf");
  };

  // ============== LOADING & ERROR VIEW ==============
  if (loading && !error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-blue-900 text-white">
        <p className="text-lg md:text-xl">Preparing your results... Please wait.</p>
      </div>
    );
  }

  if (error || !summary) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-blue-900 text-white p-4">
        <p className="text-lg md:text-xl text-red-400 mb-4">
          {error || "Unable to load summary."}
        </p>
        <button
          onClick={handleGoDashboard}
          className="bg-yellow-500 text-blue-900 font-bold px-6 py-2 rounded-full shadow-lg text-sm md:text-base"
        >
          Go to Dashboard
        </button>
      </div>
    );
  }

  const { overall_average, experiences } = summary;

  // Determine top low-score areas (for recommendations)
  const sortedByScore = [...experiences].sort(
    (a, b) => a.avg_score - b.avg_score
  );
  const lowAreas = sortedByScore.filter((e) => e.avg_score < 3.5).slice(0, 3);

  // ============== MAIN COMPACT VIEW ==============
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-blue-900 via-blue-700 to-blue-500 text-white px-2 md:px-4 py-2">
      <motion.div
        ref={summaryRef}
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-6xl bg-white text-blue-900 rounded-2xl shadow-2xl p-4 md:p-5 lg:p-6"
      >
        {/* HEADER + STUDENT INFO */}
        <div className="text-center mb-3">
          <h1 className="text-2xl md:text-3xl font-extrabold text-blue-800 leading-tight">
            Teaching Experience Summary
          </h1>
          <p className="text-xs md:text-sm text-gray-600">
            Results of your Teaching Experience self-assessment.
          </p>

          <div className="mt-2 text-xs md:text-sm text-gray-700">
            {studentInfo.name && (
              <p>
                <span className="font-semibold">Name:</span>{" "}
                {studentInfo.name}
              </p>
            )}
            {studentInfo.nim && (
              <p>
                <span className="font-semibold">Student ID (NIM):</span>{" "}
                {studentInfo.nim}
              </p>
            )}
            {!studentInfo.name && !studentInfo.nim && (
              <p>
                <span className="font-semibold">Student ID:</span>{" "}
                {studentId}
              </p>
            )}
          </div>
        </div>

        <hr className="mb-3 border-gray-200" />

        {/* TOP SECTION: OVERALL SCORE + TABLE */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 items-stretch">
          {/* Overall Score Card */}
          <motion.div
            initial={{ opacity: 0, x: -15 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="bg-blue-50 p-3 md:p-4 rounded-xl border-2 border-yellow-400 flex flex-col justify-center"
          >
            <h2 className="text-sm md:text-base font-semibold mb-1 text-center">
              Overall Score (1–5 Scale)
            </h2>

            <motion.p
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 110 }}
              className="text-3xl md:text-4xl font-black text-blue-800 text-center my-2"
            >
              {overall_average.toFixed(2)}
            </motion.p>

            <p className="text-xs md:text-sm text-gray-700 text-center mb-1">
              Level:{" "}
              <span className="font-bold">
                {getScoreLabel(overall_average)}
              </span>
            </p>

            <div className="w-full h-2.5 bg-blue-200 rounded-full overflow-hidden mb-1">
              <div
                className={`${getScoreColor(
                  overall_average
                )} h-2.5 rounded-full`}
                style={{
                  width: `${(overall_average / 5) * 100}%`,
                }}
              ></div>
            </div>

            <p className="text-[10px] text-gray-600 text-center">
              1 = {SCALE_LABELS[1]} &nbsp;·&nbsp; 5 = {SCALE_LABELS[5]}
            </p>
          </motion.div>

          {/* Category Breakdown Table */}
          <motion.div
            initial={{ opacity: 0, x: 15 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.15 }}
            className="md:col-span-2"
          >
            <h2 className="text-sm md:text-base font-bold mb-2 flex items-center text-blue-800">
              <FaChartBar className="mr-2 text-yellow-500" /> Category Breakdown
            </h2>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-sm text-xs md:text-sm">
                <thead>
                  <tr className="bg-blue-800 text-white">
                    <th className="py-1.5 px-2 text-left w-10">No</th>
                    <th className="py-1.5 px-2 text-left">Category</th>
                    <th className="py-1.5 px-2 text-right w-20">
                      Avg. Score
                    </th>
                    <th className="py-1.5 px-2 text-right w-32">
                      Level
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {experiences.map((exp, index) => {
                    const avg = exp.avg_score;
                    return (
                      <tr
                        key={exp.experience_id}
                        className={
                          index % 2 === 0 ? "bg-gray-50" : "bg-white"
                        }
                      >
                        <td className="py-1 px-2">
                          {exp.experience_id}
                        </td>
                        <td className="py-1 px-2">
                          {EXPERIENCE_LABELS[exp.experience_id]}
                        </td>
                        <td className="py-1 px-2 text-right font-semibold">
                          {avg.toFixed(2)}
                        </td>
                        <td className="py-1 px-2 text-right">
                          <span className="text-[11px] font-semibold">
                            {getScoreLabel(avg)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </motion.div>
        </div>

        {/* MIDDLE SECTION: INTERPRETATION + RECOMMENDATIONS */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mt-3">
          {/* Interpretation */}
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-3 text-xs md:text-sm">
            <h3 className="font-bold text-blue-800 mb-1">
              How to Interpret Your Score
            </h3>
            <ul className="list-disc list-inside space-y-1">
              <li>
                <span className="font-semibold">High / Very High</span> –
                strong and consistent teaching practice in that category.
              </li>
              <li>
                <span className="font-semibold">Moderate</span> – acceptable
                performance with room for improvement.
              </li>
              <li>
                <span className="font-semibold">Low / Very Low</span> –
                area that may require additional support, strategies, or
                professional development.
              </li>
            </ul>
          </div>

          {/* Recommendations */}
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-3 text-xs md:text-sm">
            <h3 className="font-bold text-blue-800 mb-1">
              Recommendations for Improvement
            </h3>
            {lowAreas.length === 0 ? (
              <p className="text-gray-700">
                Your scores are generally high across all categories. You can
                continue maintaining your current good practices and explore
                advanced professional development opportunities.
              </p>
            ) : (
              <ul className="list-disc list-inside space-y-1">
                {lowAreas.map((area) => (
                  <li key={area.experience_id}>
                    <span className="font-semibold">
                      {EXPERIENCE_LABELS[area.experience_id]}:
                    </span>{" "}
                    {RECOMMENDATION_TEXT[area.experience_id] ||
                      "Focus on gradually improving this category through practice, reflection, and targeted training."}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* BUTTONS */}
        <div className="flex flex-col md:flex-row items-center justify-center md:justify-between gap-3 mt-4">
          <button
            onClick={handleExportPDF}
            className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold px-5 py-2 rounded-full shadow-md text-xs md:text-sm"
          >
            <FaFilePdf /> Export as PDF
          </button>

          <button
            onClick={handleGoDashboard}
            className="flex items-center gap-2 bg-blue-800 hover:bg-blue-700 text-white font-bold px-7 py-2 rounded-full shadow-md text-xs md:text-sm"
          >
            <FaCheckCircle /> Finish and Return to Dashboard
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default TeachingExperienceSummary;
